import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "./PayrollPrintIsolationTest.css";

const SAMPLE_LOGO_SRC = `${process.env.PUBLIC_URL || ""}/logo512.png`;

const TABLE_ROWS = [
  {
    date: "Jun 20, 2026",
    project: "Sample Project",
    classification: "Carpenter",
    payrollType: "Standard",
    hours: "8.00",
    break: "0.00",
    rate: "$35.00",
    gross: "$280.00",
  },
  {
    date: "Jun 19, 2026",
    project: "Sample Project",
    classification: "Carpenter",
    payrollType: "Standard",
    hours: "9.88",
    break: "0.00",
    rate: "$35.00",
    gross: "$345.45",
  },
];

function logIsolationLayout(phase) {
  const root = document.getElementById("payroll-print-isolation-root");
  const doc = document.querySelector(".isolation-print-doc");
  const table = document.querySelector(".isolation-print-table");

  const readBox = (el) => {
    if (!el) return null;
    const cs = window.getComputedStyle(el);
    const rect = el.getBoundingClientRect();
    return {
      id: el.id || null,
      className: el.className || null,
      offsetWidth: el.offsetWidth,
      clientWidth: el.clientWidth,
      boundingWidth: Math.round(rect.width),
      width: cs.width,
      maxWidth: cs.maxWidth,
      position: cs.position,
      parentClassName: el.parentElement?.className || null,
      parentId: el.parentElement?.id || null,
      parentTag: el.parentElement?.tagName || null,
    };
  };

  const payload = {
    phase,
    printMediaMatches: window.matchMedia("print").matches,
    landscapeMediaMatches: window.matchMedia("(orientation: landscape)").matches,
    stylesheetCount: document.styleSheets.length,
    bodyClasses: document.body.className,
    viewport: { innerWidth: window.innerWidth, innerHeight: window.innerHeight },
    rootParentIsBody: root?.parentElement === document.body,
    root: readBox(root),
    document: readBox(doc),
    table: readBox(table),
  };

  // #region agent log
  if (process.env.NODE_ENV !== "production") {
    fetch("http://127.0.0.1:7939/ingest/7351eb42-ca54-4693-97ac-948245c2a42c", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Debug-Session-Id": "52ff66",
      },
      body: JSON.stringify({
        sessionId: "52ff66",
        runId: "isolation-test",
        hypothesisId: "isolation-print-engine",
        location: "PayrollPrintIsolationTest.js:logIsolationLayout",
        message: "Standalone print isolation snapshot",
        data: payload,
        timestamp: Date.now(),
      }),
    }).catch(() => {});
  }
  // #endregion
}

function IsolationPrintDocument() {
  return (
    <article className="isolation-print-doc" aria-label="Payroll Print Isolation Test">
      <header className="isolation-print-header">
        <img src={SAMPLE_LOGO_SRC} alt="" className="isolation-print-logo" />
        <div>
          <div className="isolation-print-brand">Organization</div>
          <h1 className="isolation-print-title">Payroll Report</h1>
        </div>
      </header>

      <hr className="isolation-print-rule" />

      <section className="isolation-print-section">
        <h2 className="isolation-print-section-title">Employee Information</h2>
        <div className="isolation-print-meta">
          <div>
            <span className="isolation-print-meta-label">Employee</span>
            <span>Joey Agostinucci</span>
          </div>
          <div>
            <span className="isolation-print-meta-label">Payroll Period</span>
            <span>June 1, 2026 – June 20, 2026</span>
          </div>
        </div>
      </section>

      <hr className="isolation-print-rule" />

      <section className="isolation-print-section">
        <h2 className="isolation-print-section-title">Payroll Detail</h2>
        <table className="isolation-print-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Project</th>
              <th>Classification</th>
              <th>Payroll Type</th>
              <th>Hours</th>
              <th>Break</th>
              <th>Rate</th>
              <th>Gross</th>
            </tr>
          </thead>
          <tbody>
            {TABLE_ROWS.map((row) => (
              <tr key={row.date}>
                <td>{row.date}</td>
                <td>{row.project}</td>
                <td>{row.classification}</td>
                <td>{row.payrollType}</td>
                <td>{row.hours}</td>
                <td>{row.break}</td>
                <td>{row.rate}</td>
                <td>{row.gross}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <div className="isolation-print-totals-wrap">
        <div className="isolation-print-totals">
          <div className="isolation-print-totals-row">
            <span>Regular Hours</span>
            <span>17.88 hrs</span>
          </div>
          <div className="isolation-print-totals-row isolation-print-totals-row--emphasis">
            <span>Gross Pay</span>
            <span>$625.45</span>
          </div>
        </div>
      </div>
    </article>
  );
}

function IsolationPrintMount({ active }) {
  if (!active || typeof document === "undefined") return null;

  return createPortal(
    <div id="payroll-print-isolation-root" aria-hidden="true">
      <IsolationPrintDocument />
    </div>,
    document.body,
  );
}

export function PayrollPrintIsolationTest({ onBack }) {
  const [printActive, setPrintActive] = useState(false);

  useEffect(() => {
    function handleBeforePrint() {
      logIsolationLayout("beforeprint");
    }

    function handleAfterPrint() {
      logIsolationLayout("afterprint");
      document.body.classList.remove("payroll-print-isolation-active");
      setPrintActive(false);
    }

    window.addEventListener("beforeprint", handleBeforePrint);
    window.addEventListener("afterprint", handleAfterPrint);

    return () => {
      window.removeEventListener("beforeprint", handleBeforePrint);
      window.removeEventListener("afterprint", handleAfterPrint);
      document.body.classList.remove("payroll-print-isolation-active");
    };
  }, []);

  const runPrintTest = useCallback(() => {
    setPrintActive(true);

    window.requestAnimationFrame(() => {
      document.body.classList.add("payroll-print-isolation-active");
      logIsolationLayout("pre-print");
      window.print();
    });
  }, []);

  return (
    <>
      <div
        style={{
          minHeight: "100vh",
          padding: "24px",
          background: "#0b1220",
          color: "white",
          fontFamily: "system-ui, sans-serif",
        }}
      >
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            style={{
              marginBottom: "16px",
              padding: "10px 16px",
              cursor: "pointer",
            }}
          >
            Back
          </button>
        )}

        <h1 style={{ marginTop: 0 }}>Payroll Print Isolation Test</h1>
        <p style={{ maxWidth: "640px", lineHeight: 1.5, opacity: 0.85 }}>
          This page renders print content directly on <code>document.body</code> via
          a React portal. It uses no CrewPilot shell, no shared report CSS, and no
          preview frame — only a standalone landscape <code>@page</code> rule.
        </p>

        <button
          type="button"
          onClick={runPrintTest}
          style={{
            marginTop: "20px",
            padding: "14px 20px",
            fontSize: "16px",
            cursor: "pointer",
          }}
        >
          Run Standalone Print Test
        </button>

        <p style={{ maxWidth: "640px", marginTop: "20px", opacity: 0.7 }}>
          Open the print preview and check whether the page is landscape and uses
          the full paper width, or still appears as a narrow portrait column.
        </p>
      </div>

      <IsolationPrintMount active={printActive} />
    </>
  );
}
